"use client";

import Cal, { getCalApi } from "@calcom/embed-react";
import { useEffect, useMemo, useRef } from "react";
import { CAL_BRAND_COLOR } from "@/lib/config";

// ---------------------------------------------------------------------------
// Calendrier cal.com intégré dans la page (pas en modal). UTM + pré-remplissage
// transmis à cal.com ; `metadata[…]` est stocké sur la réservation, donc
// visible dans le webhook cal.com côté n8n. À la réservation, le parent reçoit
// les données brutes de l'event (lues par @/lib/cal → readCalBooking).
// ---------------------------------------------------------------------------

export function CalInline({
  namespace,
  calLink,
  onBooked,
  prefill,
  utms,
  metadata,
  className,
}: {
  namespace: string;
  calLink: string;
  onBooked: (data: unknown) => void;
  prefill?: { name?: string; email?: string };
  utms?: Record<string, string>;
  metadata?: Record<string, string>;
  className?: string;
}) {
  const onBookedRef = useRef(onBooked);
  onBookedRef.current = onBooked;

  useEffect(() => {
    let cancelled = false;
    (async function () {
      const cal = await getCalApi({ namespace });
      if (cancelled) return;
      cal("ui", {
        cssVarsPerTheme: {
          light: { "cal-brand": CAL_BRAND_COLOR },
          dark: { "cal-brand": CAL_BRAND_COLOR },
        },
        hideEventTypeDetails: false,
        layout: "month_view",
      });
      cal("on", {
        action: "bookingSuccessful",
        callback: (e: { detail?: { data?: unknown } }) => onBookedRef.current(e?.detail?.data),
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [namespace]);

  const config = useMemo(() => {
    const c: Record<string, string> = {
      layout: "month_view",
      useSlotsViewOnSmallScreen: "true",
    };
    if (prefill?.name) c.name = prefill.name;
    if (prefill?.email) c.email = prefill.email;
    if (utms) for (const [k, v] of Object.entries(utms)) if (v) c[k] = v;
    if (metadata) for (const [k, v] of Object.entries(metadata)) if (v) c[`metadata[${k}]`] = v;
    return c;
  }, [prefill?.name, prefill?.email, utms, metadata]);

  return (
    <Cal
      // Nouvelle clé quand le pré-remplissage arrive (popup) : l'iframe recharge
      // avec les coordonnées déjà saisies.
      key={`${prefill?.name ?? ""}|${prefill?.email ?? ""}`}
      namespace={namespace}
      calLink={calLink}
      className={className}
      style={{ width: "100%", height: "100%", overflow: "scroll" }}
      config={config}
    />
  );
}
